import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArchive,
  faCheckCircle,
  faClock,
  faEnvelope,
  faPlay,
} from '@fortawesome/free-solid-svg-icons';
import { Box, Button, Skeleton, Tooltip } from '@mui/material';

import { WorkItem } from '../api/workItems';
import { DomainConfig } from '../api/domains';
import { getWorkItemCardClasses } from '../utils/workItemStyling';
import {
  dueBadgeColorClassFromYmd,
  dueBadgeFromYmd,
  extractYmdFromDue,
} from '../utils/userTime';
import {
  getWorkItemDisplayTitle,
  getWorkItemSecondaryLine,
  getWorkItemSourceInfo,
  getWorkItemStateLabel,
  getWorkItemTypeLabel,
} from '../utils/workItemDisplay';
import { derivedTasksLabel } from '../utils/drawerChromeCopy';
import { isSourceHomeItem } from '../utils/sourceFamily';

export const MAX_VISIBLE_PER_BUCKET = 25;

export type BucketKey = 'now' | 'next' | 'triage' | 'waiting';

export type WorkItemStylingOptions = {
  userTimeZone?: string | null;
  todayYmd?: string | null;
};

export function TodayColumnSkeletonBody() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          className="p-4 rounded-xl"
          sx={{
            backgroundColor: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
          }}
          aria-hidden
        >
          <Skeleton variant="text" width="62%" height={18} />
          <Skeleton variant="rounded" width={88} height={16} sx={{ borderRadius: 99, mt: 1 }} />
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
            <Skeleton variant="rounded" width={56} height={18} />
            <Skeleton variant="rounded" width={44} height={18} />
          </Box>
          <Skeleton variant="text" width="85%" height={14} sx={{ mt: 1.5 }} />
        </Box>
      ))}
    </>
  );
}

export interface TodayColumnProps {
  bucket: BucketKey;
  title: string;
  subtitle: string;
  items: WorkItem[];
  wipLimit?: number;
  isLoading: boolean;
  emptySlot: React.ReactNode;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  domains: DomainConfig[];
  workItemStylingOptions: WorkItemStylingOptions;
  todayYmd: string;
  focusedId: string | null;
  /** Card whose drawer is open. Soft teal glow on that card only. */
  openDrawerId?: string | null;
  selectedIds: Set<string>;
  onCardClick: (item: WorkItem) => (e: React.MouseEvent) => void;
  onInlineAction: (e: React.MouseEvent) => void;
  onCheckboxClick: (id: string, e: React.MouseEvent) => void;
  onOpenDrawer: (item: WorkItem, activator?: HTMLElement | null) => void;
  onStart: (id: string) => void;
  onDone: (id: string) => void;
  onSnooze: (id: string) => void;
  onArchive: (id: string) => void;
}

/**
 * Module-level column so React keeps the overflow node across Today page
 * re-renders (sync polls, selection, briefing collapse). An inner function
 * component would remount on every parent render and reset card scroll to the first item.
 */
export const TodayColumn = React.memo(function TodayColumn({
  bucket,
  title,
  subtitle,
  items,
  wipLimit,
  isLoading,
  emptySlot,
  isExpanded,
  onToggleExpanded,
  domains,
  workItemStylingOptions,
  todayYmd,
  focusedId,
  openDrawerId = null,
  selectedIds,
  onCardClick,
  onInlineAction,
  onCheckboxClick,
  onOpenDrawer,
  onStart,
  onDone,
  onSnooze,
  onArchive,
}: TodayColumnProps) {
  const visible = isExpanded ? items : items.slice(0, MAX_VISIBLE_PER_BUCKET);
  const showToggle = !isLoading && items.length > MAX_VISIBLE_PER_BUCKET;
  const isOverWip = Boolean(wipLimit && items.length > wipLimit);

  return (
    <div
      data-testid={`today-column-${bucket}`}
      className={`backdrop-blur-sm border rounded-2xl p-3 flex flex-col h-full transition-colors ${isOverWip ? 'border-red-500/20' : 'border-theme'}`}
      style={{
        backgroundColor: 'var(--color-bg-card)',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <div className="flex items-start justify-between mb-4 shrink-0 px-2">
        <div>
          <div className="flex items-baseline gap-2 mb-0.5">
            <h2 className="text-theme font-semibold text-lg tracking-tight">{title}</h2>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${isOverWip ? 'bg-red-500/10 text-danger' : ''}`}
              style={{ backgroundColor: isOverWip ? undefined : 'var(--color-bg-tertiary)', color: isOverWip ? undefined : 'var(--color-text-secondary)' }}
            >
              {items.length}{wipLimit ? ` / ${wipLimit}` : ''}
            </span>
          </div>
          <p className="text-[10px] text-theme-muted uppercase tracking-widest">{subtitle}</p>
        </div>
        {showToggle && (
          <Button size="small" variant="text" onClick={onToggleExpanded} sx={{ color: 'var(--color-text-muted)', textTransform: 'none', minWidth: 'auto', fontSize: '11px', fontWeight: 600, px: 1.5, py: 0.5 }}>
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        )}
      </div>

      <div
        data-testid={`today-column-${bucket}-scroll`}
        className="space-y-3 overflow-auto flex-1 scrollbar-thin pb-4 mt-2"
        style={{ maxHeight: 'calc(100vh - 240px)' }}
      >
        {isLoading ? (
          <TodayColumnSkeletonBody />
        ) : visible.length === 0 ? (
          emptySlot
        ) : (
          visible.map(item => {
            const dueYmd = extractYmdFromDue(item.due_date);
            const styling = getWorkItemCardClasses(item, domains, workItemStylingOptions);
            const titleText = getWorkItemDisplayTitle(item);
            const secondaryLineText = getWorkItemSecondaryLine(item);
            const typeLabel = getWorkItemTypeLabel(item.type);
            const stateLabel = getWorkItemStateLabel(item.state);
            const isDecision = (item.meta as { decision_needed?: boolean } | undefined)?.decision_needed;
            const sourceInfo = getWorkItemSourceInfo(item);
            const isEmailSourced = sourceInfo.sourceType === 'email';
            const isDocumentSourced = sourceInfo.sourceType === 'document';
            const isManualSourced =
              sourceInfo.sourceType === 'manual' || sourceInfo.sourceType === 'unknown';
            const derivedTasks = isSourceHomeItem(item) ? null : derivedTasksLabel(item);
            const sourceHomeCue = isSourceHomeItem(item);
            const drawerOpenOnCard = openDrawerId != null && openDrawerId === item.id;

            return (
              <div
                key={item.id}
                data-today-card-item-id={item.id}
                data-drawer-open={drawerOpenOnCard ? 'true' : undefined}
                tabIndex={-1}
                onClick={onCardClick(item)}
                className={`p-4 cursor-pointer group relative rounded-xl transition-all motion-reduce:transition-none overflow-hidden ${styling.borderClasses} ${focusedId === item.id && !drawerOpenOnCard ? 'border-indigo-500/50 ring-1 ring-indigo-500/20' : ''}`}
                style={{
                  backgroundColor: 'var(--color-bg-card)',
                  boxShadow: drawerOpenOnCard
                    ? '0 0 0 3px rgba(var(--color-accent-rgb), 0.35), var(--shadow-card)'
                    : 'var(--shadow-card)',
                  borderLeftColor: styling.domain.color,
                  ...(drawerOpenOnCard ? { outline: '2px solid rgba(var(--color-accent-rgb), 0.55)' } : {}),
                }}
              >
                <div
                  className="absolute -right-8 -top-8 w-16 h-16 blur-2xl opacity-0 group-hover:opacity-20 transition-opacity motion-reduce:transition-none motion-reduce:group-hover:opacity-0 rounded-full"
                  style={{ backgroundColor: styling.domain.color }}
                ></div>

                <div className="flex items-start gap-3">
                  <div className="pt-1" onClick={onInlineAction}>
                    <input
                      type="checkbox"
                      className="accent-indigo-500 w-5 h-5 md:w-3.5 md:h-3.5 opacity-60 md:opacity-40 md:group-hover:opacity-100 transition-opacity motion-reduce:transition-none"
                      checked={selectedIds.has(item.id)}
                      onClick={e => onCheckboxClick(item.id, e)}
                      readOnly
                    />
                  </div>

                  <div className="min-w-0 flex-1 relative z-10">
                    {sourceHomeCue ? <span hidden data-testid="today-source-home-cue" /> : null}
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className={`text-sm font-medium leading-tight truncate flex-1 min-w-0 ${isDecision ? 'text-chip-info' : 'text-theme'}`}>{titleText}</h3>
                      {isDecision && (
                        <span className="flex h-1.5 w-1.5 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)] mt-1"></span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] mb-2">
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border font-bold min-w-0 max-w-[180px]"
                        style={{
                          backgroundColor: 'var(--color-bg-tertiary)',
                          borderColor: 'var(--color-border)',
                          color: 'var(--color-text-secondary)'
                        }}
                        title={styling.domain.label}
                      >
                        <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: styling.domain.color }} aria-hidden></span>
                        <span className="shrink-0" aria-hidden>{styling.domain.icon}</span>
                        <span className="truncate">{styling.domain.label}</span>
                      </span>
                      {isEmailSourced && (
                        <>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-emerald-500/15 text-chip-success border border-emerald-500/30">
                            Synced
                          </span>
                          <button
                            type="button"
                            data-testid="today-card-open-email"
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-sky-500/15 text-chip-info border border-sky-500/30 hover:bg-sky-500/25"
                            onClick={event => {
                              event.stopPropagation();
                              onOpenDrawer(item, event.currentTarget);
                            }}
                            aria-label="Open original email"
                          >
                            <FontAwesomeIcon icon={faEnvelope} className="text-[10px]" aria-hidden />
                            Original
                          </button>
                        </>
                      )}
                      {isDocumentSourced && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-sky-500/15 text-chip-info border border-sky-500/30">
                          Upload
                        </span>
                      )}
                      {isManualSourced && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-violet-500/15 text-chip-violet border border-violet-500/30">
                          Manual
                        </span>
                      )}
                      {item.type && item.type !== 'APPLICATION' && typeLabel && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold text-theme-muted border" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border)' }}>
                          {typeLabel}
                        </span>
                      )}
                      {item.type && item.type !== 'APPLICATION' && stateLabel && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold text-theme-muted border" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border)' }}>
                          {stateLabel}
                        </span>
                      )}
                      {derivedTasks && (
                        <span
                          data-testid="today-card-derived-tasks"
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium text-theme-muted"
                          style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                        >
                          {derivedTasks}
                        </span>
                      )}
                      {secondaryLineText && <span className="text-theme-muted truncate max-w-[100px]">{secondaryLineText}</span>}
                      {dueYmd && (
                        <span
                          className={`${dueBadgeColorClassFromYmd(dueYmd, todayYmd)} font-medium`}
                        >
                          {dueBadgeFromYmd(dueYmd, todayYmd)}
                        </span>
                      )}
                    </div>

                    {item.summary && <p className="text-[11px] text-theme-secondary leading-relaxed line-clamp-2">{item.summary}</p>}
                  </div>
                </div>

                <div
                  className="mt-3 flex items-center justify-between pt-3 translate-y-0 opacity-100 md:translate-y-2 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100 motion-reduce:md:translate-y-0 motion-reduce:md:opacity-100 transition-all duration-300 motion-reduce:transition-none"
                  style={{ borderTop: '1px solid var(--color-border)' }}
                >
                  <div className="flex items-center gap-1" onClick={onInlineAction}>
                    {bucket === 'triage' && (
                      <Tooltip title="Start">
                        <button
                          type="button"
                          onClick={() => onStart(item.id)}
                          className="h-11 w-11 md:h-auto md:w-auto md:p-1.5 flex items-center justify-center hover:bg-theme-card rounded-xl transition-colors"
                          aria-label="Start"
                        >
                          <FontAwesomeIcon icon={faPlay} className="w-4 h-4 md:w-2.5 md:h-2.5 text-theme-muted" />
                        </button>
                      </Tooltip>
                    )}
                    {!sourceHomeCue && (
                    <Tooltip title="Done">
                      <button
                        type="button"
                        onClick={() => onDone(item.id)}
                        className="h-11 w-11 md:h-auto md:w-auto md:p-1.5 flex items-center justify-center hover:bg-theme-card rounded-xl transition-colors"
                        aria-label="Mark done"
                      >
                        <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 md:w-2.5 md:h-2.5 text-theme-muted" />
                      </button>
                    </Tooltip>
                    )}
                    <Tooltip title="Snooze">
                      <button
                        type="button"
                        onClick={() => onSnooze(item.id)}
                        className="h-11 w-11 md:h-auto md:w-auto md:p-1.5 flex items-center justify-center hover:bg-theme-card rounded-xl transition-colors"
                        aria-label="Snooze 1 day"
                      >
                        <FontAwesomeIcon icon={faClock} className="w-4 h-4 md:w-2.5 md:h-2.5 text-theme-muted" />
                      </button>
                    </Tooltip>
                    <Tooltip title="Archive">
                      <button
                        type="button"
                        onClick={() => onArchive(item.id)}
                        className="h-11 w-11 md:h-auto md:w-auto md:p-1.5 flex items-center justify-center hover:bg-theme-card rounded-xl transition-colors"
                        aria-label="Archive"
                      >
                        <FontAwesomeIcon icon={faArchive} className="w-4 h-4 md:w-2.5 md:h-2.5 text-theme-muted" />
                      </button>
                    </Tooltip>
                  </div>
                  <div className="text-[10px] text-theme-muted font-medium">VIEW DETAILS</div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
});
